import React, { useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { executeResolutionApi } from '@api/gameApi';
import { exitTargetingMode } from '@services/actionService';
import { exitDeployMode } from '@services/deployService';

// Hooks
import useGame from '@/hooks/useGame';
import useGameStore from "@store/useGameStore";
import useSelectionStore from "@store/useSelectionStore";
import useConfigStore from "@store/useConfigStore";
import useShortcutContext from '@/hooks/useShortcutContext';
import { ClickDTO } from '@models/ClickDTO';
import { ClickableType, ClickContext, GameStatus } from '@utils/enums';
import useCriticalErrorStore from "@store/useCriticalErrorStore";

// Componentes y Gestores de UI
import MainMenu from "@components/ui/game/main-menu/MainMenu";
import PlayerStatusPanel from "@components/game/panels/PlayerStatusPanel";
import MapContainer from "@containers/MapContainer";
import PhaseContainer from "@containers/PhaseContainer";
import InfoPanel from "@components/game/panels/InfoPanel/InfoPanel";
import Loader from '@/components/ui/general/loader/Loader';
import Drawer from '@components/ui/Drawer';
import BottomDrawerContent from '@components/game/panels/BottomDrawerContent/BottomDrawerContent';
import CriticalErrorBanner from '@components/ui/general/CriticalErrorBanner';

import { useT } from '@/i18n';
import AudioManager from '@audio/AudioManager';
import { Storage } from '@storage';

import "./GameView.css";

function GameView() {
    const { fetchGameById } = useGame();
    const { gameId } = useParams();
    const t = useT();

    // Subscribe to store data
    const game = useGameStore((state) => state.game);

    useEffect(() => {
        if (gameId) {
            fetchGameById(gameId);
        }
    }, [gameId, fetchGameById]);

    // Start battle music playlist when game loads
    useEffect(() => {
        if (!game) return;

        const loadBattleMusic = async () => {
            const tracks = await Storage.get('assets', 'music:battle');

            if (!tracks || tracks.length === 0) {
                throw new Error('[GameView] No battle music tracks in Storage. Backend should send _assets.music');
            }

            console.log(`[GameView] Starting battle playlist with ${tracks.length} tracks`);
            AudioManager.playPlaylist('battle', tracks);
        };

        loadBattleMusic();

        // Cleanup: stop playlist when leaving GameView
        return () => {
            AudioManager.stopPlaylist();
        };
    }, [game?.id]); // Only trigger on game load, not on every update

    const loading = useGameStore((state) => state.loading);
    const error = useGameStore((state) => state.error);
    // Derive resolving state from game status
    const isResolving = game?.status === GameStatus.RESOLVING;

    // Drawer states
    const leftDrawerOpen = useSelectionStore((state) => state.leftDrawerOpen);
    const rightDrawerOpen = useSelectionStore((state) => state.rightDrawerOpen);
    const bottomDrawerOpen = useSelectionStore((state) => state.bottomDrawerOpen);
    const toggleLeftDrawer = useSelectionStore((state) => state.toggleLeftDrawer);
    const toggleRightDrawer = useSelectionStore((state) => state.toggleRightDrawer);
    const toggleBottomDrawer = useSelectionStore((state) => state.toggleBottomDrawer);
    const setDrawerOpen = useSelectionStore((state) => state.setDrawerOpen);

    // Critical error state (standby mode)
    const hasCriticalError = useCriticalErrorStore((state) => state.hasCriticalError);

    // Current click context for shortcut handling
    const clickContext = useSelectionStore((state) => state.clickContext);
    const openSettings = useConfigStore((state) => state.openModal);

    // Shortcut contexts based on current game mode
    // Priority: Targeting > Deploy > Default (drawers/settings)
    const isTargeting = clickContext === ClickContext.TARGET_ACTION;
    const isDeploying = clickContext === ClickContext.DEPLOY_UNIT;

    // Track which drawer to open next (for Alt+Escape rotation)
    const nextDrawerRef = useRef('bottom'); // bottom → right → left → bottom...

    // Check if any drawer is open
    const hasAnyDrawerOpen = leftDrawerOpen || rightDrawerOpen || bottomDrawerOpen;

    // Handler: ESC closes drawers one by one (left → right → bottom), then opens settings
    const handleGameCancel = useCallback(() => {
        if (leftDrawerOpen) {
            setDrawerOpen('left', false);
        } else if (rightDrawerOpen) {
            setDrawerOpen('right', false);
        } else if (bottomDrawerOpen) {
            setDrawerOpen('bottom', false);
        } else {
            openSettings();
        }
    }, [leftDrawerOpen, rightDrawerOpen, bottomDrawerOpen, setDrawerOpen, openSettings]);

    // Handler: Alt+ESC opens drawers in rotation (bottom → right → left)
    const handleToggleNextDrawer = useCallback(() => {
        const current = nextDrawerRef.current;

        if (current === 'bottom') {
            setDrawerOpen('bottom', true);
            nextDrawerRef.current = 'right';
        } else if (current === 'right') {
            setDrawerOpen('right', true);
            nextDrawerRef.current = 'left';
        } else {
            setDrawerOpen('left', true);
            nextDrawerRef.current = 'bottom';
        }
    }, [setDrawerOpen]);

    useShortcutContext(
        'game:targeting',
        { cancel: useCallback(() => exitTargetingMode(), []) },
        isTargeting
    );

    useShortcutContext(
        'game:deploy',
        { cancel: useCallback(() => exitDeployMode(), []) },
        isDeploying
    );

    useShortcutContext(
        'page:game',
        {
            cancel: handleGameCancel,
            toggleNextDrawer: handleToggleNextDrawer,
        },
        !isTargeting && !isDeploying
    );

    // Auto-select first unit when game loads (selections are cleared in api.js interceptor)
    const handleClick = useSelectionStore((state) => state.handleClick);

    // Track previous game status to detect phase transitions
    const previousStatusRef = useRef(null);

    useEffect(() => {
        if (!game || loading) return;

        const gameStatus = game.status;
        const previousStatus = previousStatusRef.current;

        // Detect DEPLOY → PLAN transition (force selection of first unit)
        const isDeployToPlanTransition = previousStatus === GameStatus.DEPLOY && gameStatus === GameStatus.PLAN;

        // Update ref for next render
        previousStatusRef.current = gameStatus;

        // Check if nothing is selected - only then auto-select
        // EXCEPTION: On DEPLOY → PLAN transition, always select first unit
        const { currentClick } = useSelectionStore.getState();
        if (currentClick && !isDeployToPlanTransition) return;

        let unitToSelect = null;

        if (gameStatus === GameStatus.DEPLOY) {
            // Deploy phase: select first available unit by energy cost (lower first)
            const availableUnits = game.available_units;
            if (availableUnits && availableUnits.length > 0) {
                const sortedUnits = [...availableUnits].sort((a, b) =>
                    a.unit_template.energy_cost - b.unit_template.energy_cost
                );
                unitToSelect = sortedUnits[0];
            }
        } else if (gameStatus === GameStatus.PLAN || gameStatus === GameStatus.RESOLVED) {
            // Plan/Cinematic phase: select first unit by initiative (lowest first = moves first)
            const units = game.dynamic_objects?.units;
            if (units && units.length > 0) {
                const sortedUnits = [...units].sort((a, b) => a.initiative - b.initiative);
                unitToSelect = sortedUnits[0];
            }
        }

        if (unitToSelect) {
            const clickType = gameStatus === GameStatus.DEPLOY
                ? ClickableType.DEPLOYABLE
                : ClickableType.UNIT;
            const clickDTO = new ClickDTO(clickType, unitToSelect, 'GameView-AutoSelect');
            handleClick(clickDTO);
        }
    }, [game?.id, game?.status, loading, handleClick]);

    // Clear targeting mode when game phase changes (prevents stale targetingAction)
    useEffect(() => {
        if (!game) return;

        const { targetingAction } = useSelectionStore.getState();
        if (targetingAction && game.status !== GameStatus.PLAN) {
            exitTargetingMode();
        }
    }, [game?.status]);

    // When game enters RESOLVING, trigger resolution execution
    useEffect(() => {
        if (isResolving && game?.id) {
            // console.log("Game is RESOLVING - AUTO-EXECUTE DISABLED FOR DEBUGGING");
            // console.log("To manually trigger resolution, run in console:");
            // console.log("executeResolutionApi(" + game.id + ")");
            
            // COMMENTED OUT FOR DEBUGGING - Uncomment to re-enable auto-execution
            // Start the resolution process - backend handles it in chunks
            const startResolution = async () => {
                try {
                    await executeResolutionApi(game.id);
                    console.log("Resolution chunk processed successfully");
                    
                    // Check if we're still resolving and need to continue
                    const { game: currentGame } = useGameStore.getState();
                    if (currentGame?.status === GameStatus.RESOLVING) {
                        // Backend is still processing, call again after 2 seconds
                        console.log("Still resolving, calling execute-resolution again...");
                        setTimeout(startResolution, 2000);
                    } else {
                        console.log("Resolution complete, status:", currentGame?.status);
                    }
                } catch (err) {
                    // Only log error if it's not the expected "already resolving" error
                    if (err?.response?.status !== 409) {
                        console.error("Error executing resolution:", err);
                    }
                    
                    // For now, still poll with execute-resolution as fallback
                    console.log("Starting fallback polling...");
                    const pollInterval = setInterval(async () => {
                        try {
                            console.log("Polling resolution status...");
                            await executeResolutionApi(game.id);
                            
                            // Check if status changed (will be handled by store update)
                            const { game: currentGame } = useGameStore.getState();
                            if (currentGame?.status !== GameStatus.RESOLVING) {
                                console.log("Game is no longer RESOLVING, stopping poll");
                                clearInterval(pollInterval);
                            }
                        } catch (err) {
                            // Silently ignore 409 errors (already processing)
                            if (err?.response?.status !== 409) {
                                console.error("Error polling resolution:", err);
                            }
                        }
                    }, 2000); // Poll every 2 seconds
                    
                    // Store interval ID for cleanup
                    return () => clearInterval(pollInterval);
                }
            };
            
            startResolution();
        }
    }, [isResolving, game?.id, fetchGameById]);

    // --- Render ---
    if (loading) return <Loader message={t('game.loading')} />;
    if (error) return <div><p style={{ color: "red" }}>{t('common.error')}: {error}</p></div>;
    if (!game) return <Loader message={t('game.waiting_data')} />;

    // Get teams from the game players array
    const team1 = game.players?.filter(p => p.pivot?.team === 1) || [];
    const team2 = game.players?.filter(p => p.pivot?.team === 2) || [];

    return (
        <div className={`game-view ${hasCriticalError ? 'game-view--standby' : ''}`}>
            {/* Critical Error Banner - renders on top of everything */}
            <CriticalErrorBanner />
            {hasCriticalError && <div className="standby-overlay" />}

            {/* Top Menu */}
            <MainMenu />
            <PhaseContainer />

            {/* Overlay while backend calculates resolution */}
            {isResolving && <Loader message={t('game.resolving')} isOverlay={true} />}

            {/* Game Area */}
            <div className={`game-area ${bottomDrawerOpen ? 'game-area--bottom-open' : ''}`}>
                {/* Left Drawer - Player Status */}
                <Drawer
                    side="left"
                    isOpen={leftDrawerOpen}
                    onToggle={toggleLeftDrawer}
                    alwaysVisibleAbove="none"
                >
                    <PlayerStatusPanel team1={team1} team2={team2} />
                </Drawer>

                {/* Map Container - Center */}
                <div className={`map-area ${leftDrawerOpen ? 'map-area--left-open' : ''} ${rightDrawerOpen ? 'map-area--right-open' : ''} ${bottomDrawerOpen ? 'map-area--bottom-open' : ''}`}>
                    <MapContainer />
                </div>

                {/* Right Drawer - Info Panel */}
                <Drawer
                    side="right"
                    isOpen={rightDrawerOpen}
                    onToggle={toggleRightDrawer}
                    alwaysVisibleAbove="none"
                >
                    <InfoPanel />
                </Drawer>

                {/* Bottom Drawer - Tabbed Content */}
                <Drawer
                    side="bottom"
                    isOpen={bottomDrawerOpen}
                    onToggle={toggleBottomDrawer}
                    alwaysVisibleAbove="none"
                >
                    <BottomDrawerContent />
                </Drawer>
            </div>
        </div>
    );
}

export default GameView;